import React, { useState, useEffect } from 'react';
import { usersAPI } from '../api/users';
import { authAPI } from '../api/auth';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { UserPlus, Trash2, Lock, Unlock, LayoutGrid, List } from 'lucide-react';
import CreateUserModal from '../components/CreateUserModal';
import EditUserModal from '../components/EditUserModal';
import LoadingSpinner from '../components/common/LoadingSpinner';

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(localStorage.getItem('usersViewMode') || 'list');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    localStorage.setItem('usersViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersAPI.getAllUsers();
      // Ensure data is an array
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on current user's role
  // retail_admin should not see super_admin or system_admin users
  const filteredUsers =
    currentUser?.role === 'retail_admin'
      ? users.filter((user) => !['super_admin', 'system_admin'].includes(user.role))
      : users;

  const handleCreateUser = async (userData, role) => {
    try {
      let response;
      if (role === 'admin') {
        response = await usersAPI.createAdmin(userData);
      } else {
        response = await usersAPI.createStaff(userData);
      }

      fetchUsers();
      return response;
    } catch (error) {
      throw error;
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }

    try {
      await usersAPI.deleteUser(userId);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleUpdateUser = async (userId, userData, action, reason) => {
    try {
      if (action === 'reset-password') {
        const response = await usersAPI.resetUserPassword(userId, null);
        fetchUsers();
        return response;
      } else if (action === 'lock') {
        await usersAPI.lockUser(userId, reason);
        fetchUsers();
      } else if (action === 'unlock') {
        await usersAPI.unlockUser(userId);
        fetchUsers();
      } else {
        await usersAPI.updateUser(userId, userData);
        fetchUsers();
      }
    } catch (error) {
      throw error;
    }
  };

  const getRoleBadge = (role) => {
    const colors = {
      super_admin: 'bg-accent-gold text-primary-black',
      system_admin: 'bg-purple-100 text-purple-800',
      retail_admin: 'bg-blue-100 text-blue-800',
      branch_admin: 'bg-indigo-100 text-indigo-800',
      branch_staff: 'bg-green-100 text-green-800',
      // Legacy roles for backwards compatibility
      admin: 'bg-blue-100 text-blue-800',
      staff: 'bg-green-100 text-green-800'
    };

    const labels = {
      super_admin: 'Super Admin',
      system_admin: 'System Admin',
      retail_admin: 'Retail Admin',
      branch_admin: 'Branch Admin',
      branch_staff: 'Branch Staff',
      // Legacy roles for backwards compatibility
      admin: 'Admin',
      staff: 'Staff'
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-semibold ${colors[role] || 'bg-gray-100 text-gray-800'}`}
      >
        {labels[role] || role}
      </span>
    );
  };

  if (loading) {
    return <LoadingSpinner message="Loading users..." className="min-h-screen" />;
  }

  return (
    <div className="min-h-screen bg-secondary-offWhite">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-primary-black">User Management</h1>
              <p className="mt-1 text-sm text-primary-slate">Manage system users and permissions</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center bg-gray-100 rounded-lg p-1 border border-gray-200">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-all ${
                    viewMode === 'list'
                      ? 'bg-white text-primary-black shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="List View"
                >
                  <List className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-all ${
                    viewMode === 'grid'
                      ? 'bg-white text-primary-black shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  title="Grid View"
                >
                  <LayoutGrid className="h-5 w-5" />
                </button>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-accent-gold text-primary-black font-semibold rounded-lg hover:bg-accent-goldDark transition-colors"
              >
                <UserPlus className="h-5 w-5" />
                Add User
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {filteredUsers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md overflow-hidden text-center py-12">
            <p className="text-gray-500 text-lg">No users found</p>
            <p className="text-gray-400 text-sm mt-2">Click "Add User" to create your first user</p>
          </div>
        ) : viewMode === 'list' ? (
          /* Users Table */
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr
                      key={user.id}
                      onClick={() => handleUserClick(user)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-accent-gold flex items-center justify-center text-primary-black font-bold">
                              {user.full_name.charAt(0).toUpperCase()}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.full_name}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{getRoleBadge(user.role)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.is_locked ? (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 flex items-center gap-1 w-fit">
                            <Lock className="h-3 w-3" />
                            Locked
                          </span>
                        ) : (
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 flex items-center gap-1 w-fit">
                            <Unlock className="h-3 w-3" />
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          {user.role !== 'super_admin' && user.id !== currentUser.id && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteUser(user.id);
                              }}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="Delete"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          )}
                          {user.role === 'super_admin' && (
                            <span className="text-xs text-gray-400 italic">Protected</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Grid View */
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                onClick={() => handleUserClick(user)}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer border border-gray-100"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex-shrink-0 h-12 w-12">
                      <div className="h-12 w-12 rounded-full bg-accent-gold flex items-center justify-center text-primary-black font-bold text-lg">
                        {user.full_name.charAt(0).toUpperCase()}
                      </div>
                    </div>
                    {getRoleBadge(user.role)}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{user.full_name}</h3>
                  <p className="text-sm text-gray-500 mb-4">{user.email}</p>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      {user.is_locked ? (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex items-center gap-1">
                          <Lock className="h-3 w-3" />
                          Locked
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 flex items-center gap-1">
                          <Unlock className="h-3 w-3" />
                          Active
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">
                        {new Date(user.created_at).toLocaleDateString()}
                      </span>
                      {user.role !== 'super_admin' && user.id !== currentUser.id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteUser(user.id);
                          }}
                          className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateUser}
        currentUserRole={currentUser?.role}
      />

      {/* Edit User Modal */}
      <EditUserModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onUpdate={handleUpdateUser}
        currentUserRole={currentUser?.role}
      />
    </div>
  );
};

export default UsersPage;
